import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CompanyChip from '../../components/asset-company/CompanyChip';
import ContractForm from '../../components/contracts/ContractForm';
import { downloadCsv, downloadXlsx } from '../../lib/exportTable';
import { shortDay } from '../../lib/licences';
import { contractsApi, errorOf, STATUS_LABEL, statusTone, nextWords, capWords, rateWords } from '../../lib/contracts';
import '../../components/Assets.css';
import '../../components/licences/Licences.css';
import '../../components/contracts/Contracts.css';

/**
 * THE CONTRACTS REGISTER (120), Ticket Mgmt -> Contractors -> Contracts.
 * Administrators.
 *
 * The licences register's shape (pages/licences/LicencesList.js): the
 * summary - active, what needs a decision within 90 days, what is near a
 * cap - the last two filters when pressed; one filter bar (search, status,
 * contractor, company, due within, near a cap, sort), all in the address so
 * the bell's links land on the right rows; the list; and the export of
 * exactly the rows the filters match. Drawn under the Contractors strip
 * (pages/ContractorsList.js), so it opens with no header of its own.
 */
const FILTERS = ['q', 'status', 'contractor', 'company', 'due', 'cap', 'sort'];

export default function ContractsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState('');
  const [query, setQuery] = useState(params.get('q') || '');

  const filters = useMemo(() => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || ''])
    .filter(([, v]) => v !== '')), [params]);
  const key = JSON.stringify(filters);

  // Only the latest request's answer is shown (the licences lesson).
  const latest = useRef(0);
  const load = useCallback(() => {
    const f = JSON.parse(key);
    const n = ++latest.current;
    return Promise.all([contractsApi.list(f), contractsApi.summary()])
      .then(([list, sum]) => { if (n !== latest.current) return; setData(list); setSummary(sum); setError(''); })
      .catch((e) => { if (n === latest.current) setError(errorOf(e, 'Could not load the contracts.')); });
  }, [key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    contractsApi.options().then(setOptions).catch(() => setOptions({ contractors: [], companies: [], people: [], categories: [] }));
  }, []);

  // The address as it is this moment, not the router's copy, which lags a
  // click behind in a transition.
  const liveParams = () => new URLSearchParams(window.location.search);

  useEffect(() => {
    const t = setTimeout(() => {
      const now = liveParams();
      if ((now.get('q') || '') === query.trim()) return;
      if (query.trim()) now.set('q', query.trim()); else now.delete('q');
      setParams(now, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k, v) => {
    const next = liveParams();
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const toggleParam = (k, v) => setParam(k, liveParams().get(k) === v ? '' : v);
  const anyFilter = FILTERS.some((k) => k !== 'sort' && params.get(k));
  const clear = () => { setQuery(''); setParams(new URLSearchParams()); };

  const rows = data?.contracts || [];
  const companies = (options?.companies || []).filter((c) => !c.archived_at);
  const closeForm = useCallback(() => setAdding(false), []);

  const exportAs = async (kind) => {
    setExporting(kind);
    try {
      const r = await contractsApi.exportRows(filters);
      const name = `contracts-${r.today || 'list'}`;
      if (kind === 'csv') downloadCsv(r.columns, r.rows, name); else downloadXlsx(r.columns, r.rows, name, 'Contracts');
    } catch (e) {
      setError(errorOf(e, 'The export did not work.'));
    }
    setExporting('');
  };

  const due = summary?.due_90;
  const first = due?.first?.[0];
  const open = (id) => navigate(`/manage/contracts/${id}`);

  return (
    <div className="con-page" data-testid="contracts-page">
      <div className="con-bar">
        <p className="page-description" style={{ margin: 0 }}>
          What each contractor has agreed to: dates, notice, the rate card and any cap.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add a contract</button>
      </div>

      {summary && (
        <div className="lic-summary" data-testid="contract-summary">
          <div className="lic-stat" data-stat="active">
            <span className="lic-stat-label">Active</span>
            <span className="lic-stat-value">{summary.active}</span>
            <span className="lic-stat-sub">{`${summary.by_status.draft} draft${summary.by_status.draft === 1 ? '' : 's'}`}</span>
          </div>
          <button type="button" className="lic-stat" data-stat="due" aria-pressed={params.get('due') === '90'}
            data-tone={due?.count ? 'warning' : undefined} onClick={() => toggleParam('due', '90')}>
            <span className="lic-stat-label">Decide within 90 days</span>
            <span className="lic-stat-value">{due?.count || 0}</span>
            <span className="lic-stat-sub">
              {first ? `First: ${first.title}, in ${first.days_left} day${first.days_left === 1 ? '' : 's'}` : 'Nothing due'}
            </span>
          </button>
          <button type="button" className="lic-stat" data-stat="cap" aria-pressed={params.get('cap') === 'true'}
            data-tone={summary.caps.reached ? 'critical' : summary.caps.near ? 'warning' : undefined} onClick={() => toggleParam('cap', 'true')}>
            <span className="lic-stat-label">Near a cap</span>
            <span className="lic-stat-value">{summary.caps.near + summary.caps.reached}</span>
            <span className="lic-stat-sub">
              {summary.caps.reached ? `${summary.caps.reached} at or over it` : summary.caps.near ? 'At 80% or more' : 'Every contract within its caps'}
            </span>
          </button>
        </div>
      )}

      <div className="as-reg-filters" role="search" aria-label="Filter the contracts">
        <div className="as-toolbar">
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Title, reference, contractor or company" aria-label="Search the contracts" />
          <select value={params.get('status') || ''} aria-label="Status" onChange={(e) => setParam('status', e.target.value)}>
            <option value="">Active and drafts</option>
            <option value="active">{`Active${summary ? ` (${summary.by_status.active})` : ''}`}</option>
            <option value="draft">{`Drafts${summary ? ` (${summary.by_status.draft})` : ''}`}</option>
            <option value="ended">{`Ended${summary ? ` (${summary.by_status.ended})` : ''}`}</option>
            <option value="cancelled">{`Cancelled${summary ? ` (${summary.by_status.cancelled})` : ''}`}</option>
            <option value="all">Every status</option>
          </select>
          <select value={params.get('contractor') || ''} aria-label="Contractor" onChange={(e) => setParam('contractor', e.target.value)}>
            <option value="">Every contractor</option>
            {(options?.contractors || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {companies.length > 0 && (
            <select value={params.get('company') || ''} aria-label="Company" onChange={(e) => setParam('company', e.target.value)}>
              <option value="">Every company</option>
              <option value="none">No particular company</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={params.get('due') || ''} aria-label="Due" onChange={(e) => setParam('due', e.target.value)}>
            <option value="">Any date</option>
            <option value="30">Within 30 days</option>
            <option value="90">Within 90 days</option>
            <option value="365">Within a year</option>
          </select>
          <label className="as-check">
            <input type="checkbox" checked={params.get('cap') === 'true'} onChange={(e) => setParam('cap', e.target.checked ? 'true' : '')} />
            Near a cap
          </label>
          <select value={params.get('sort') || ''} aria-label="Sort" onChange={(e) => setParam('sort', e.target.value)}>
            <option value="">Soonest date first</option>
            <option value="contractor">By contractor</option>
            <option value="title">By title</option>
            <option value="used">Most of a cap used</option>
          </select>
          {anyFilter && <button type="button" className="btn btn-plain btn-small" onClick={clear}>Clear filters</button>}
        </div>
      </div>

      {error && <p className="as-error" role="alert">{error}</p>}

      {data && (
        <div className="as-reg-results">
          <span data-testid="contract-count">{`${data.total} contract${data.total === 1 ? '' : 's'}`}</span>
          {data.total > 0 && (
            <span className="lic-results-actions">
              <button type="button" className="btn btn-plain btn-small" disabled={Boolean(exporting)} onClick={() => exportAs('csv')}>
                {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
              </button>
              <button type="button" className="btn btn-plain btn-small" disabled={Boolean(exporting)} onClick={() => exportAs('xlsx')}>
                {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
              </button>
            </span>
          )}
        </div>
      )}

      {!data && !error && <p className="as-quiet" aria-busy="true">Loading the contracts…</p>}

      {data && rows.length === 0 ? (
        <div className="empty-state" data-testid="contracts-empty">
          <p>{anyFilter ? 'No contracts match these filters.' : 'No contracts yet. Add the first one - the contractor you use most.'}</p>
        </div>
      ) : data && (
        <div className="as-table-wrap">
          <table className="as-table as-reg-table lic-table con-table">
            <thead>
              <tr>
                <th>Contract</th><th>Contractor</th><th>Rate today</th><th>Dates</th><th>Next</th><th>Caps</th>
                {companies.length > 0 && <th>Company</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => {
                const nx = nextWords(k);
                const hours = capWords(k.cap.hours, 'hours', k.currency);
                const money = capWords(k.cap.amount, 'amount', k.currency);
                return (
                  <tr key={k.id} data-contract={k.id} onClick={() => open(k.id)}>
                    <td data-label="Contract">
                      <a href={`/manage/contracts/${k.id}`} className="lic-product"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(k.id); }}>
                        {k.title}
                      </a>
                      {k.status !== 'active' && (
                        <span className="lic-flags"><span className="as-chip" data-tone={statusTone(k.status)}>{STATUS_LABEL[k.status]}</span></span>
                      )}
                      <span className="as-sub">{k.reference || ' '}</span>
                    </td>
                    <td data-label="Contractor">{k.contractor_name}</td>
                    <td data-label="Rate today">{rateWords(k.rate_today, k.currency) || <span className="as-quiet">Their own</span>}</td>
                    <td data-label="Dates">
                      {k.start_date ? `${shortDay(k.start_date)} – ${k.end_date ? shortDay(k.end_date) : 'open'}` : <span className="as-quiet">—</span>}
                      {k.notice_deadline && <span className="as-sub">{`Notice by ${shortDay(k.notice_deadline)}`}</span>}
                    </td>
                    <td data-label="Next">
                      {nx ? <span className="as-chip" data-tone={nx.tone}>{nx.text}</span> : <span className="as-quiet">—</span>}
                      {k.auto_renew && k.status === 'active' && <span className="as-sub">Renews by itself</span>}
                    </td>
                    <td data-label="Caps">
                      {!hours && !money && <span className="as-quiet">None</span>}
                      {[['hours', hours], ['amount', money]].filter(([, c]) => c).map(([kind, c]) => (
                        <span key={kind} className="con-cap" data-cap={kind}>
                          <span className="as-chip" data-tone={c.tone}>{c.text}</span>
                          <span className="lic-bar" data-tone={c.tone === 'success' ? undefined : c.tone} aria-hidden="true">
                            <span style={{ width: `${Math.min(100, c.percent)}%` }} />
                          </span>
                        </span>
                      ))}
                    </td>
                    {companies.length > 0 && (
                      <td data-label="Company">
                        <CompanyChip name={k.company_name} color={k.company_color} code={k.company_code} none="—" />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <ContractForm options={options} onClose={closeForm}
          onSaved={(saved) => { setAdding(false); open(saved.contract.id); }} />
      )}
    </div>
  );
}
